import dayjs from 'dayjs';
import validator from 'validator';
import { textLengthValidator } from '../functions';

export const INVALID_PHONE_NUMBER_MESSAGE = "Invalid Phone Number";
export const INVALID_EMAIL_MESSAGE = "Invalid E-mail";
export const INVALID_CURRENCY = "Invalid Currency";
export const INVALID_DATE = "Invalid Date";
export const INVALID_TIME = "Invalid Time";
export const CURRENCY_MAX_LIMIT = "Currency exceeds max limit";
export const EXCEEDED_STRING_LENGTH = "Exceeds max length of 50";
export const INVALID_DATE_TIME = "Invalid date/time";
export const INVALID_FUTURE_DATE = "Future date not allowed";
export const ERROR_MESSAGE = "No value provided";
export const NOTE_TO_SELF_MAX_LIMIT = "Exceeds max length of 100";

export type ValidatorResponse = {
  isValid: boolean;
  errorMessage?: string;
};

export const dateValidator = (value: string): ValidatorResponse => {
  if (!value || validator.isDate(value) || value === "") {
    return { isValid: true, errorMessage: "" };
  }
  return { isValid: false, errorMessage: INVALID_DATE };
}

export const contactDateValidator = (value: string): ValidatorResponse => {
  if (!value  || value === "") {
    return { isValid: true, errorMessage: "" };
  }
  if (validator.isDate(value, { format: "YYYY-MM-DD", strictMode: true }) ||
      validator.isDate(value, { format: "MM-DD-YYYY", strictMode: true })) {
   if (dayjs(value, ["YYYY-MM-DD", "MM-DD-YYYY"], true) <= dayjs()) {
      return { isValid: true, errorMessage: "" };
    }
    return { isValid: false, errorMessage: INVALID_FUTURE_DATE };
  }
  return { isValid: false, errorMessage: INVALID_DATE };
}

export const timeValidator = (value: string): ValidatorResponse => {
  if (!value ||
    validator.isTime(value, { mode: "default" }) || // Validate HH:MM
    validator.isTime(value, { mode: "withSeconds" }) // Validate HH:MM:SS
  ) {
    return { isValid: true, errorMessage: "" };
  }
  return { isValid: false, errorMessage: INVALID_TIME };
}

export const dateTimeValidator = (value: string | null | undefined): ValidatorResponse => {
  if (!value || value === "") return { isValid: true, errorMessage: "" };

  const [datePart, timePart] = value.split(" ");
  const dateOk = validator.isDate(datePart, { format: "YYYY-MM-DD", strictMode: true }) ||
    validator.isDate(datePart, { format: "MM-DD-YYYY", strictMode: true });
  
  if (!dateOk) {
    return { isValid: false, errorMessage: INVALID_DATE };
  }

  const timeOk =
    validator.isTime(timePart, { mode: "default" }) ||
    validator.isTime(timePart, { mode: "withSeconds" });

  if (!timeOk) {
    return { isValid: false, errorMessage: INVALID_TIME };
  }
  return { isValid: true, errorMessage: "" };
};

export const currencyValidator = (value: string): ValidatorResponse => {
  if (!value || value === "$") {
    return { isValid: true, errorMessage: "" };
  }

  if (validator.isCurrency(value, { allow_negatives: false })) {
    const numericValue = value.replace(/[^0-9.]/g, "");
    if (numericValue.split(".")[0].length > 10) {
      return { isValid: false, errorMessage: CURRENCY_MAX_LIMIT };
    }
    return { isValid: true, errorMessage: "" };
  }
  return { isValid: false, errorMessage: INVALID_CURRENCY };
};

export const emailValidator = (value: string): ValidatorResponse => {
  if (!value || validator.isEmail(value)) {
    return { isValid: true, errorMessage: "" };
  }
  return { isValid: false, errorMessage: INVALID_EMAIL_MESSAGE };
}

export const stringValidator = textLengthValidator(50, EXCEEDED_STRING_LENGTH);

/* This validator is used for the Note to Self field in the Homesite Details page. */
export const noteToSelfValidator = textLengthValidator(100, NOTE_TO_SELF_MAX_LIMIT);

export const phoneValidator = (value: string): ValidatorResponse => {
  if (!value) {
    return { isValid: true, errorMessage: "" };
  }

  value = value.replace(/\./g, ""); // Edge case in phone number comes in with a XXX.XXX.XXXX format.
  const match = value.match(/^(.*?)(?:\s*(x|ext|e)\s*(\d+))?$/i);
  if (!match) {
    return { isValid: false, errorMessage: INVALID_PHONE_NUMBER_MESSAGE };
  }
  const phone = match[1]?.trim(); // Main phone number
  const extKeyword = match[2]; // Extension keyword (if present)
  const ext = match[3]; // Extension digits (if present)
  const isPhoneValid = phone && validator.isMobilePhone(phone, "en-US", { strictMode: false });

  const isExtensionValid = !extKeyword || (ext && ext.length <= 6 && /^\d+$/.test(ext));
  if (isPhoneValid && isExtensionValid) {
    return { isValid: true, errorMessage: "" };
  }
  return { isValid: false, errorMessage: INVALID_PHONE_NUMBER_MESSAGE };
};

export const normaliseDate = (raw: string | null | undefined) : string => {
      if (!raw) return "";
      if (validator.isDate(raw, { format: "YYYY-MM-DD", strictMode: true })) {
        return dayjs(raw, "YYYY-MM-DD", true).format("YYYY-MM-DD");
      }
      if (validator.isDate(raw, { format: "MM-DD-YYYY", strictMode: true })) {
        return dayjs(raw, "MM-DD-YYYY", true).format("YYYY-MM-DD");
      }
      return "";
    };

export const normalizeDateTime = (raw: string | null | undefined) : string => {
  if (dateTimeValidator(raw).isValid) {
    if (!raw) return "";
    const date = dayjs(raw, ["YYYY-MM-DD HH:mm", "MM-DD-YYYY HH:mm"], true);
    if (date.isValid()) {
      return date.format("YYYY-MM-DD HH:mm");
    }
  }
  return "";
};
